import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react'
import {
  Button,
  Icon,
  ToastNotifications,
} from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import {
  useHotkeysService,
  useRegisterManager,
  useSaleManager,
  useUserManager,
  useUserSwitchingService,
} from '../../../../ng-compat/hooks'
import { PERMISSIONS } from '../../../../constants'
import {
  isEmptySale,
  isLegacyServiceSale,
  isMarkedAsUnfulfilled,
} from '../../../../functions/sale'
import { hasServiceOrder } from '../../../../functions/service'
import {
  selectCurrentSale,
  selectLineItems,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { useAppSelector } from '../../../../state/hooks'
import { containsGiftCard } from '../../../../functions/lineItem'
import { $rootScope } from '../../../../ng-compat/imports'
import { AddNoteModal } from './AddNoteModal'

type Props = {
  shouldDisableActions: boolean
}

const Hotkey = 'f5'

export const ParkSale: FunctionComponent<Props> = ({
  shouldDisableActions,
}) => {
  const t = useT()
  const sale = useAppSelector(selectCurrentSale)
  const lineItems = useAppSelector(selectLineItems)

  const saleManager = useSaleManager()
  const userSwitchingService = useUserSwitchingService()
  const userManager = useUserManager()
  const registerManager = useRegisterManager()
  const hotkeysService = useHotkeysService()

  const [addNoteModalOpen, setAddNoteModalOpen] = useState(false)

  const isDisabled =
    !userManager.hasPermission(PERMISSIONS.SALE_PARKED) ||
    isEmptySale(sale) ||
    shouldDisableActions ||
    isMarkedAsUnfulfilled(sale.status) ||
    hasServiceOrder(sale.serviceFields, sale.customFields) ||
    isLegacyServiceSale(sale.status)

  const register = registerManager.getActiveRegister()
  const registerNotePreference = register && register.ask_for_note_on_save
  const shouldPromptForNote =
    registerNotePreference === 2 || registerNotePreference === 1

  const parkSale = useCallback(async () => {
    await saleManager.parkSale()
    setAddNoteModalOpen(false)
    await userSwitchingService.switchUserIfNeeded()

    // Need to use $rootScope here as the product search field is angularJS
    // TODO: Refactor to when product search is Reactified
    $rootScope.$emit('autocomplete:global-search:focus')
  }, [saleManager, userSwitchingService])

  // Handle when the park sale action button is clicked
  // Does validation and determines whether to open the add note modal
  const handleParkSale = useCallback(async () => {
    if (containsGiftCard(lineItems)) {
      ToastNotifications.negative(
        t(
          `Sales with gift cards can’t be parked. Remove the gift card, then park the sale.`
        )
      )
      return
    }

    if (shouldPromptForNote) {
      setAddNoteModalOpen(true)
      return
    }

    await parkSale()
  }, [lineItems, shouldPromptForNote, parkSale, t])

  useEffect(() => {
    if (isDisabled) {
      hotkeysService.remove(Hotkey)

      return
    }
    hotkeysService.add({
      action: () => handleParkSale(),
      combo: Hotkey,
    })

    return () => {
      hotkeysService.remove(Hotkey)
    }
  }, [isDisabled, handleParkSale, hotkeysService])

  return (
    <>
      <Button
        modifier="text"
        variant="supplementary"
        disabled={isDisabled}
        onClick={handleParkSale}
      >
        <div className="vd-flex vd-flex--align-center">
          <Icon icon="vd-i-park" className="vd-i vd-mr1" />
          {t('Park sale', { _context: 'Button to open park sale popover' })}
        </div>
      </Button>
      {addNoteModalOpen && (
        <AddNoteModal
          initialNote={sale.note}
          onClose={() => setAddNoteModalOpen(false)}
          onParkSale={parkSale}
        />
      )}
    </>
  )
}
